import type { VendorSnapshot } from '@/lib/api'

// ─── Premium template id selector ───────────────────────────────────────────
// Lives outside the premium barrel so lightweight consumers (policy chrome,
// modal theme) can read the template decision without statically importing the
// layout components — the barrel reaches all three layouts, which would defeat
// lazy chunking.

export type PremiumTemplateId = 'classic' | 'modern' | 'luxe' | 'editorial'

/** Published template ids — the ones an operator can actually select in
 *  TripzOperator admin (and that survive the snapshot's `@IsEnum`). Editorial
 *  is deliberately absent: it is design-preview only until it is wired. */
const PUBLISHED: readonly PremiumTemplateId[] = ['classic', 'modern', 'luxe']

/** The tenant's active premium template id — the SINGLE source of the
 *  template decision, shared by the home layout and the policy pages so
 *  secondary surfaces always match the homepage chrome. */
export function selectPremiumTemplateId(snapshot: VendorSnapshot): PremiumTemplateId {
  // Design-review escape hatch only. The published template contract remains
  // classic | modern | luxe until Editorial is approved and wired in
  // TripzOperator.
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('themePreview') === 'editorial'
  ) {
    return 'editorial'
  }
  const id = snapshot.tierConfig?.templateId as PremiumTemplateId | undefined
  return id && PUBLISHED.includes(id) ? id : 'classic'
}
